import { useCallback, useEffect, useMemo, useState, type ChangeEvent, type ComponentType, type ReactNode } from "react";
import { useApp } from "@/store/useApp";
import { CLIENT_TYPE_LABELS, documentLabel, isPessoaFisica, legalNameLabel } from "@/lib/clientTypeLabels";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  cancelPasswordResetAdminCustomer,
  deleteAdminCustomer,
  fetchAdminCustomer,
  fetchAdminCustomers,
  fetchCustomerDeletionImpact,
  forcePasswordResetAdminCustomer,
  toAdminClientSummaryFromDetail,
  updateAdminCustomerProfile,
  updateAdminCustomerStatus,
  updateAdminCustomerFinancialCondition,
  type CustomerDeletionImpact,
  type UpdateAdminCustomerProfileInput,
} from "@/lib/adminCustomersApi";
import { fetchPriceTables } from "@/lib/adminPriceTablesApi";
import { formatCompanyAddress } from "@/lib/formatCompanyAddress";
import { fetchVendors, type VendorSummary } from "@/lib/adminReportsApi";
import {
  AlertTriangle,
  Ban,
  Building2,
  Calendar,
  CheckCircle2,
  ChevronDown,
  Clock,
  FileText,
  KeyRound,
  Loader2,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Search,
  Settings,
  Trash2,
  User,
  UserCheck,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import { hasPermission, type AdminClientDetail, type AdminClientSummary, type ClientType, type CommercialStatus, type PriceTable } from "@/lib/types";
import { NegotiationConditionPanel } from "@/components/admin/NegotiationConditionPanel";

// "INCOMPLETO" é um filtro derivado (não é um status real): cadastros iniciados sem ficha comercial.
type ClientFilter = "ALL" | CommercialStatus | "INCOMPLETO";

const STATUS_FILTERS: { id: ClientFilter; label: string }[] = [
  { id: "VALIDADO", label: "Validados" },
  { id: "PENDENTE_VALIDACAO", label: "Pendentes" },
  { id: "INCOMPLETO", label: "Incompletos" },
  { id: "REJEITADO", label: "Rejeitados" },
  { id: "BLOQUEADO", label: "Bloqueados" },
  { id: "ALL", label: "Todos" },
];

type LoadState = "idle" | "loading" | "ready" | "error";

export default function AdminClients() {
  const account = useApp((s) => s.account);
  const canEdit = hasPermission(account, "CLIENTES.CLIENTE.EDITAR_PERFIL");
  const canValidate = hasPermission(account, "CLIENTES.CLIENTE.VALIDAR");
  const canDelete = hasPermission(account, "CLIENTES.CLIENTE.EXCLUIR");
  const [clients, setClients] = useState<AdminClientSummary[]>([]);
  const [clientsStatus, setClientsStatus] = useState<LoadState>("idle");
  const [clientsError, setClientsError] = useState<string | null>(null);
  const [priceTables, setPriceTables] = useState<PriceTable[]>([]);
  const [priceTablesStatus, setPriceTablesStatus] = useState<LoadState>("idle");
  const [vendors, setVendors] = useState<VendorSummary[]>([]);
  const [vendorsStatus, setVendorsStatus] = useState<LoadState>("idle");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedClient, setSelectedClient] = useState<AdminClientDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  // Senha temporária exibida UMA ÚNICA VEZ após o reset admin — nunca é relistada pela API.
  const [tempPasswordInfo, setTempPasswordInfo] = useState<{ label: string; password: string } | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [editClient, setEditClient] = useState<AdminClientDetail | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [filter, setFilter] = useState<ClientFilter>("VALIDADO");
  const [query, setQuery] = useState("");
  const [deleteTarget, setDeleteTarget] = useState<AdminClientSummary | null>(null);
  const [deleteImpact, setDeleteImpact] = useState<CustomerDeletionImpact | null>(null);
  const [deleteImpactLoading, setDeleteImpactLoading] = useState(false);
  const [deleteImpactError, setDeleteImpactError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const loadClients = useCallback(async () => {
    setClientsStatus("loading");
    setClientsError(null);
    try {
      const summaries = await fetchAdminCustomers();
      setClients(summaries);
      setClientsStatus("ready");
    } catch (error) {
      setClients([]);
      setClientsStatus("error");
      setClientsError(error instanceof Error ? error.message : "Nao foi possivel carregar os clientes");
    }
  }, []);

  const ensurePriceTables = useCallback(async () => {
    // Consulta auxiliar: só busca tabelas se a conta puder vê-las (evita 403 no escopo de quem só
    // gerencia clientes). Sem a permissão, a lista fica vazia e a atribuição de tabela não é oferecida.
    if (!hasPermission(useApp.getState().account, "PRECOS.TABELA.VISUALIZAR")) {
      setPriceTablesStatus("ready");
      return [];
    }
    if (priceTablesStatus === "ready") {
      return priceTables;
    }

    setPriceTablesStatus("loading");
    try {
      const summaries = await fetchPriceTables();
      setPriceTables(summaries);
      setPriceTablesStatus("ready");
      return summaries;
    } catch (error) {
      setPriceTables([]);
      setPriceTablesStatus("error");
      throw error;
    }
  }, [priceTables, priceTablesStatus]);

  const ensureVendors = useCallback(async () => {
    if (vendorsStatus === "ready") {
      return vendors;
    }
    setVendorsStatus("loading");
    try {
      const summaries = await fetchVendors();
      setVendors(summaries);
      setVendorsStatus("ready");
      return summaries;
    } catch {
      setVendors([]);
      setVendorsStatus("error");
      return [];
    }
  }, [vendors, vendorsStatus]);

  const openClient = useCallback(
    async (clientId: string) => {
      setSelectedId(clientId);
      setSelectedClient(null);
      setDetailError(null);
      setDetailLoading(true);

      try {
        const [detail] = await Promise.all([fetchAdminCustomer(clientId), ensurePriceTables(), ensureVendors()]);
        setSelectedClient(detail);
      } catch (error) {
        setDetailError(error instanceof Error ? error.message : "Nao foi possivel carregar o cliente");
      } finally {
        setDetailLoading(false);
      }
    },
    [ensurePriceTables, ensureVendors],
  );

  const openEdit = useCallback(async (clientId: string) => {
    setEditId(clientId);
    setEditClient(null);
    setEditError(null);
    setEditLoading(true);
    try {
      const detail = await fetchAdminCustomer(clientId);
      setEditClient(detail);
    } catch (error) {
      setEditError(error instanceof Error ? error.message : "Nao foi possivel carregar o cliente");
    } finally {
      setEditLoading(false);
    }
  }, []);

  const closeEdit = () => {
    setEditId(null);
    setEditClient(null);
    setEditError(null);
    setEditLoading(false);
  };

  const openDelete = useCallback(async (client: AdminClientSummary) => {
    setDeleteTarget(client);
    setDeleteImpact(null);
    setDeleteImpactError(null);
    setDeleteImpactLoading(true);
    try {
      const impact = await fetchCustomerDeletionImpact(client.id);
      setDeleteImpact(impact);
    } catch (error) {
      setDeleteImpactError(error instanceof Error ? error.message : "Nao foi possivel carregar os vinculos do cliente");
    } finally {
      setDeleteImpactLoading(false);
    }
  }, []);

  const closeDelete = () => {
    if (deleting) return;
    setDeleteTarget(null);
    setDeleteImpact(null);
    setDeleteImpactError(null);
    setDeleteImpactLoading(false);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await deleteAdminCustomer(deleteTarget.id);
      setClients((current) => current.filter((item) => item.id !== deleteTarget.id));
      toast.success(`${getClientLabel(deleteTarget)} excluído`);
      setDeleteTarget(null);
      setDeleteImpact(null);
      setDeleteImpactError(null);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Nao foi possivel excluir o cliente");
    } finally {
      setDeleting(false);
    }
  };

  const saveProfile = async (clientId: string, input: UpdateAdminCustomerProfileInput) => {
    try {
      const updated = await updateAdminCustomerProfile(clientId, input);
      setClients((current) =>
        current.map((item) => (item.id === updated.id ? toAdminClientSummaryFromDetail(updated) : item)),
      );
      toast.success(`${getClientLabel(updated)} atualizado`);
      closeEdit();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Nao foi possivel atualizar o cliente");
    }
  };

  useEffect(() => {
    void loadClients();
  }, [loadClients]);

  const filtered = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return clients.filter((client) => {
      const isIncomplete = client.status === "PENDENTE_VALIDACAO" && client.hasProfile === false;
      if (filter === "INCOMPLETO") {
        if (!isIncomplete) return false;
      } else {
        if (filter !== "ALL" && client.status !== filter) return false;
        // Cadastro abandonado (sem ficha) sai da fila de validação — fica na aba "Incompletos".
        if (filter === "PENDENTE_VALIDACAO" && isIncomplete) return false;
      }
      if (!normalizedQuery) return true;
      // O documento (CNPJ ou CPF) é comparado por dígitos para casar com ou sem
      // pontuação (ex.: "12345678000199").
      const queryDigits = normalizedQuery.replace(/\D/g, "");
      const cnpjDigits = (client.cnpj ?? "").replace(/\D/g, "");
      return (
        (client.tradeName ?? "").toLowerCase().includes(normalizedQuery) ||
        (client.companyName ?? "").toLowerCase().includes(normalizedQuery) ||
        client.email.toLowerCase().includes(normalizedQuery) ||
        (client.cnpj ?? "").toLowerCase().includes(normalizedQuery) ||
        (queryDigits.length > 0 && cnpjDigits.includes(queryDigits))
      );
    });
  }, [clients, filter, query]);

  const counts = useMemo(
    () => ({
      ALL: clients.length,
      PENDENTE_VALIDACAO: clients.filter(
        (client) => client.status === "PENDENTE_VALIDACAO" && client.hasProfile !== false,
      ).length,
      INCOMPLETO: clients.filter(
        (client) => client.status === "PENDENTE_VALIDACAO" && client.hasProfile === false,
      ).length,
      VALIDADO: clients.filter((client) => client.status === "VALIDADO").length,
      REJEITADO: clients.filter((client) => client.status === "REJEITADO").length,
      BLOQUEADO: clients.filter((client) => client.status === "BLOQUEADO").length,
    }),
    [clients],
  );

  const closeDialog = () => {
    setSelectedId(null);
    setSelectedClient(null);
    setDetailError(null);
    setDetailLoading(false);
  };

  const changeStatus = async (
    client: AdminClientDetail,
    status: CommercialStatus,
    clientType?: ClientType,
    priceTableId?: string | null,
    vendorId?: string | null,
    commissionPercent?: number | null,
    smallQuantitySurchargeEnabled?: boolean,
  ) => {
    try {
      const updated = await updateAdminCustomerStatus(
        client.id,
        status,
        clientType,
        priceTableId,
        vendorId,
        commissionPercent,
        smallQuantitySurchargeEnabled,
      );
      setClients((current) =>
        current.map((item) => (item.id === updated.id ? toAdminClientSummaryFromDetail(updated) : item)),
      );
      toast.success(`${getClientLabel(updated)} atualizado para ${status.toLowerCase()}`);
      closeDialog();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Nao foi possivel atualizar o status do cliente");
    }
  };

  const changeStatusFromSummary = async (clientId: string, status: CommercialStatus) => {
    try {
      const detail = await fetchAdminCustomer(clientId);
      await changeStatus(
        detail,
        status,
        detail.profile?.clientType ?? detail.clientType,
        detail.priceTableId ?? undefined,
        detail.vendorId ?? undefined,
        detail.commissionPercent ?? undefined,
        detail.smallQuantitySurchargeEnabled ?? undefined,
      );
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Nao foi possivel atualizar o status do cliente");
    }
  };

  const togglePasswordReset = async (client: AdminClientSummary, required: boolean) => {
    try {
      if (required) {
        const { customer, temporaryPassword } = await forcePasswordResetAdminCustomer(client.id);
        setClients((current) =>
          current.map((item) => (item.id === customer.id ? toAdminClientSummaryFromDetail(customer) : item)),
        );
        // Mostra a senha temporária uma única vez para o admin repassar ao cliente.
        setTempPasswordInfo({ label: getClientLabel(customer), password: temporaryPassword });
      } else {
        const updated = await cancelPasswordResetAdminCustomer(client.id);
        setClients((current) =>
          current.map((item) => (item.id === updated.id ? toAdminClientSummaryFromDetail(updated) : item)),
        );
        toast.success(`Redefinição de senha cancelada para ${getClientLabel(updated)}`);
      }
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Nao foi possivel atualizar a redefinição de senha");
    }
  };

  return (
    <>
      <section className="bg-gradient-hero text-primary-foreground">
        <div className="container py-8">
          <h1 className="font-display text-3xl font-bold tracking-tight">Cadastro de Clientes</h1>
          <p className="mt-1 text-sm text-primary-foreground/80">
            Gerencie e acompanhe os cadastros e status dos clientes.
          </p>
          <div className="mt-6 grid max-w-2xl grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { label: "Pendentes", value: counts.PENDENTE_VALIDACAO, icon: Clock },
              { label: "Validados", value: counts.VALIDADO, icon: CheckCircle2 },
              { label: "Rejeitados", value: counts.REJEITADO, icon: XCircle },
              { label: "Bloqueados", value: counts.BLOQUEADO, icon: Ban },
            ].map((stat) => (
              <div
                key={stat.label}
                className="rounded-xl border border-primary-foreground/20 bg-primary-foreground/10 p-3 backdrop-blur-sm"
              >
                <stat.icon className="mb-2 h-4 w-4 opacity-80" />
                <div className="font-display text-2xl font-bold tabular-nums">{stat.value}</div>
                <div className="text-xs opacity-80">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="container space-y-5 py-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Buscar por nome, CNPJ/CPF ou email"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="h-11 bg-surface pl-9"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {STATUS_FILTERS.map((statusOption) => (
              <Button
                key={statusOption.id}
                variant="chip"
                size="sm"
                data-active={filter === statusOption.id}
                onClick={() => setFilter(statusOption.id)}
                className="shrink-0"
              >
                {statusOption.label}
                <span className="ml-1 tabular-nums opacity-70">({counts[statusOption.id]})</span>
              </Button>
            ))}
          </div>
        </div>

        <div className="surface-card overflow-hidden">
          {clientsStatus === "loading" && (
            <div className="px-5 py-10 text-center text-sm text-muted-foreground">Carregando clientes...</div>
          )}

          {clientsStatus === "error" && (
            <div className="px-5 py-10 text-center text-sm text-destructive">
              {clientsError ?? "Nao foi possivel carregar os clientes"}
            </div>
          )}

          {clientsStatus !== "loading" && clientsStatus !== "error" && (
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <colgroup>
                  <col className="w-[38%]" />
                  <col className="w-[14%]" />
                  <col className="w-[14%]" />
                  <col className="w-[14%]" />
                  <col className="w-[20%]" />
                </colgroup>
                <thead className="bg-secondary/40 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-5 py-3 text-left">Empresa</th>
                    <th className="px-5 py-3 text-left">Tipo</th>
                    <th className="px-5 py-3 text-left">Status</th>
                    <th className="px-5 py-3 text-left">Cadastro</th>
                    <th className="px-5 py-3 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((client) => (
                    <ClientRow
                      key={client.id}
                      client={client}
                      canEdit={canEdit}
                      canValidate={canValidate}
                      canDelete={canDelete}
                      onOpen={() => void openClient(client.id)}
                      onEdit={() => void openEdit(client.id)}
                      onValidate={() => void openClient(client.id)}
                      onReject={() => void changeStatusFromSummary(client.id, "REJEITADO")}
                      onBlock={() => void changeStatusFromSummary(client.id, "BLOQUEADO")}
                      onDelete={() => void openDelete(client)}
                      onTogglePasswordReset={(required) => void togglePasswordReset(client, required)}
                    />
                  ))}
                  {filtered.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-5 py-12 text-center text-sm text-muted-foreground">
                        Nenhum cliente encontrado neste filtro.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <ValidateDialog
        open={selectedId !== null}
        client={selectedClient}
        loading={detailLoading}
        error={detailError}
        priceTables={priceTables}
        priceTablesStatus={priceTablesStatus}
        vendors={vendors}
        vendorsStatus={vendorsStatus}
        onClose={closeDialog}
        onConfirm={changeStatus}
      />

      <EditClientDialog
        open={editId !== null}
        client={editClient}
        loading={editLoading}
        error={editError}
        onClose={closeEdit}
        onSave={saveProfile}
      />

      <DeleteClientDialog
        open={deleteTarget !== null}
        target={deleteTarget}
        impact={deleteImpact}
        loading={deleteImpactLoading}
        error={deleteImpactError}
        deleting={deleting}
        onClose={closeDelete}
        onConfirm={confirmDelete}
      />

      <TemporaryPasswordDialog info={tempPasswordInfo} onClose={() => setTempPasswordInfo(null)} />
    </>
  );
}

function ClientRow({
  client,
  canEdit,
  canValidate,
  canDelete,
  onOpen,
  onEdit,
  onValidate,
  onReject,
  onBlock,
  onDelete,
  onTogglePasswordReset,
}: {
  client: AdminClientSummary;
  canEdit: boolean;
  canValidate: boolean;
  canDelete: boolean;
  onOpen: () => void;
  onEdit: () => void;
  onValidate: () => void;
  onReject: () => void;
  onBlock: () => void;
  onDelete: () => void;
  onTogglePasswordReset: (required: boolean) => void;
}) {
  return (
    <tr
      className="cursor-pointer border-t border-border align-top transition-colors hover:bg-secondary/20"
      onClick={onOpen}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onOpen();
        }
      }}
    >
      <td className="px-5 py-4">
        <div className="min-w-0 space-y-1">
          <div className="flex min-w-0 items-center gap-2">
            <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate font-display font-semibold">{getClientLabel(client)}</span>
          </div>
          <div className="truncate text-xs text-muted-foreground">{client.email}</div>
          {client.cnpj && (
            <div className="truncate text-xs text-muted-foreground">
              {documentLabel(client.clientType)}: {client.cnpj}
            </div>
          )}
          {client.passwordResetRequired && (
            <div className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700">
              <KeyRound className="h-3 w-3" /> Redefinir senha pendente
            </div>
          )}
        </div>
      </td>
      <td className="px-5 py-4 text-sm">
        {client.clientType ? CLIENT_TYPE_LABELS[client.clientType] : "Pendente"}
      </td>
      <td className="px-5 py-4">
        <StatusBadge status={client.status} incomplete={client.hasProfile === false} />
      </td>
      <td className="px-5 py-4 text-sm text-muted-foreground">{new Date(client.createdAt).toLocaleDateString("pt-BR")}</td>
      <td className="px-5 py-4">
        <ClientRowActions
          client={client}
          canEdit={canEdit}
          canValidate={canValidate}
          canDelete={canDelete}
          onEdit={onEdit}
          onValidate={onValidate}
          onReject={onReject}
          onBlock={onBlock}
          onDelete={onDelete}
          onTogglePasswordReset={onTogglePasswordReset}
        />
      </td>
    </tr>
  );
}

function ClientRowActions({
  client,
  canEdit,
  canValidate,
  canDelete,
  onEdit,
  onValidate,
  onReject,
  onBlock,
  onDelete,
  onTogglePasswordReset,
}: {
  client: AdminClientSummary;
  canEdit: boolean;
  canValidate: boolean;
  canDelete: boolean;
  onEdit: () => void;
  onValidate: () => void;
  onReject: () => void;
  onBlock: () => void;
  onDelete: () => void;
  onTogglePasswordReset: (required: boolean) => void;
}) {
  const showEdit = canEdit;
  const showValidate = canValidate;
  const showReject = canValidate && client.status !== "REJEITADO";
  const showBlock = canValidate && client.status !== "BLOQUEADO";
  const showDelete = canDelete && client.status === "BLOQUEADO";
  const showPasswordReset = canEdit;
  const resetRequired = client.passwordResetRequired === true;

  const hasCadastro = showEdit;
  const hasValidacao = showValidate || showReject || showBlock;

  if (!hasCadastro && !hasValidacao && !showPasswordReset && !showDelete) {
    return <div className="flex justify-end text-sm text-muted-foreground">—</div>;
  }

  return (
    <div className="flex justify-end" onClick={(event) => event.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" variant="outline">
            <Settings className="h-4 w-4" /> Ações <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          {hasCadastro && (
            <>
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Cadastro</DropdownMenuLabel>
              {showEdit && (
                <DropdownMenuItem onSelect={onEdit}>
                  <Pencil className="mr-2 h-4 w-4" /> Editar
                </DropdownMenuItem>
              )}
            </>
          )}
          {hasValidacao && (
            <>
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Validação</DropdownMenuLabel>
              {showValidate && (
                <DropdownMenuItem onSelect={onValidate}>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  {client.status === "VALIDADO" ? "Revalidar" : "Validar"}
                </DropdownMenuItem>
              )}
              {showReject && (
                <DropdownMenuItem onSelect={onReject}>
                  <XCircle className="mr-2 h-4 w-4" /> Rejeitar
                </DropdownMenuItem>
              )}
              {showBlock && (
                <DropdownMenuItem onSelect={onBlock}>
                  <Ban className="mr-2 h-4 w-4" /> Bloquear
                </DropdownMenuItem>
              )}
            </>
          )}
          {showPasswordReset && (
            <>
              <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">Acesso</DropdownMenuLabel>
              <DropdownMenuItem onSelect={() => onTogglePasswordReset(!resetRequired)}>
                <KeyRound className="mr-2 h-4 w-4" />
                {resetRequired ? "Cancelar redefinição de senha" : "Gerar senha temporária"}
              </DropdownMenuItem>
            </>
          )}
          {showDelete && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={onDelete}
                className="text-destructive focus:bg-destructive/10 focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" /> Excluir
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

/** Exibe a senha temporária gerada pelo admin UMA ÚNICA VEZ (não é relistada). */
function TemporaryPasswordDialog({
  info,
  onClose,
}: {
  info: { label: string; password: string } | null;
  onClose: () => void;
}) {
  const copy = () => {
    if (info?.password) {
      void navigator.clipboard?.writeText(info.password).then(
        () => toast.success("Senha copiada"),
        () => toast.error("Não foi possível copiar"),
      );
    }
  };
  return (
    <Dialog open={info !== null} onOpenChange={(value) => { if (!value) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5 text-primary" /> Senha temporária gerada
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Repasse esta senha a <span className="font-medium text-foreground">{info?.label}</span> por um
            canal seguro. Ela é exibida <span className="font-medium text-foreground">apenas uma vez</span> e
            o cliente precisará trocá-la ao entrar.
          </p>
          <div className="flex items-center gap-2">
            <code className="flex-1 rounded-md border bg-muted px-3 py-2 font-mono text-base tracking-wider">
              {info?.password}
            </code>
            <Button type="button" variant="outline" onClick={copy}>Copiar</Button>
          </div>
        </div>
        <DialogFooter>
          <Button type="button" onClick={onClose}>Concluído</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteClientDialog({
  open,
  target,
  impact,
  loading,
  error,
  deleting,
  onClose,
  onConfirm,
}: {
  open: boolean;
  target: AdminClientSummary | null;
  impact: CustomerDeletionImpact | null;
  loading: boolean;
  error: string | null;
  deleting: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const blocked = impact != null && !impact.deletable;
  const canProceed = impact != null && impact.deletable && !loading && !deleting;
  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" /> Excluir cliente
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Esta ação é <strong>irreversível</strong>. Tudo listado abaixo será removido junto do cliente{" "}
            <strong>{target ? getClientLabel(target) : ""}</strong>.
          </p>

          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Carregando vínculos…
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900">{error}</div>
          )}

          {blocked && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              {impact?.blockReason}
            </div>
          )}

          {impact && (
            <div className="space-y-3 rounded-lg border border-border p-3">
              {impact.groups.map((group) => (
                <div key={group.key}>
                  <div className="flex items-center justify-between text-sm font-medium">
                    <span>{group.label}</span>
                    <span className="tabular-nums text-muted-foreground">{group.count}</span>
                  </div>
                  {group.items.length > 0 && (
                    <ul className="mt-1 list-disc space-y-0.5 pl-5 text-xs text-muted-foreground">
                      {group.items.map((item, index) => (
                        <li key={index} className="truncate">{item}</li>
                      ))}
                      {group.count > group.items.length && (
                        <li>… e mais {group.count - group.items.length}</li>
                      )}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={deleting}>Cancelar</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={!canProceed}>
            {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
            Excluir definitivamente
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ValidateDialog({
  open,
  client,
  loading,
  error,
  priceTables,
  priceTablesStatus,
  vendors,
  vendorsStatus,
  onClose,
  onConfirm,
}: {
  open: boolean;
  client: AdminClientDetail | null;
  loading: boolean;
  error: string | null;
  priceTables: PriceTable[];
  priceTablesStatus: LoadState;
  vendors: VendorSummary[];
  vendorsStatus: LoadState;
  onClose: () => void;
  onConfirm: (
    client: AdminClientDetail,
    status: CommercialStatus,
    clientType?: ClientType,
    priceTableId?: string | null,
    vendorId?: string | null,
    commissionPercent?: number | null,
    smallQuantitySurchargeEnabled?: boolean,
  ) => Promise<void>;
}) {
  const NO_PRICE_TABLE = "__none__";
  const NO_VENDOR = "__none__";
  const [clientType, setClientType] = useState<ClientType>("RESTAURANTE");
  const [priceTableId, setPriceTableId] = useState<string>(NO_PRICE_TABLE);
  const [vendorId, setVendorId] = useState<string>(NO_VENDOR);
  const [commissionInput, setCommissionInput] = useState<string>("");
  const [surchargeEnabled, setSurchargeEnabled] = useState<boolean>(true);
  const NO_METHOD = "__none__";
  const [termDaysInput, setTermDaysInput] = useState<string>("");
  const [paymentMethod, setPaymentMethod] = useState<string>(NO_METHOD);
  const [installmentsInput, setInstallmentsInput] = useState<string>("1");
  const [savingCondition, setSavingCondition] = useState<boolean>(false);

  useEffect(() => {
    setClientType(client?.profile?.clientType ?? client?.clientType ?? "RESTAURANTE");
    setPriceTableId(client?.priceTableId ?? NO_PRICE_TABLE);
    setVendorId(client?.vendorId ?? NO_VENDOR);
    setCommissionInput(client?.commissionPercent != null ? String(client.commissionPercent) : "");
    setSurchargeEnabled(client?.smallQuantitySurchargeEnabled ?? true);
    setTermDaysInput(client?.defaultPaymentTermDays != null ? String(client.defaultPaymentTermDays) : "");
    setPaymentMethod(client?.defaultPaymentMethod ?? NO_METHOD);
    setInstallmentsInput(client?.defaultInstallments != null ? String(client.defaultInstallments) : "1");
  }, [client]);

  const saveFinancialCondition = async () => {
    if (!client) return;
    const term = termDaysInput.trim() === "" ? null : Number(termDaysInput);
    if (term != null && (Number.isNaN(term) || term < 0)) {
      toast.error("Prazo inválido");
      return;
    }
    const installments = installmentsInput.trim() === "" ? 1 : Number(installmentsInput);
    if (Number.isNaN(installments) || installments < 1) {
      toast.error("Quantidade de parcelas inválida");
      return;
    }
    setSavingCondition(true);
    try {
      await updateAdminCustomerFinancialCondition(client.id, {
        defaultPaymentTermDays: term,
        defaultPaymentMethod: paymentMethod === NO_METHOD ? null : (paymentMethod as AdminClientDetail["defaultPaymentMethod"]),
        defaultInstallments: installments,
      });
      toast.success("Condição financeira salva");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao salvar condição financeira");
    } finally {
      setSavingCondition(false);
    }
  };

  const selectedPriceTableId = priceTableId === NO_PRICE_TABLE ? null : priceTableId;
  const selectedVendorId = vendorId === NO_VENDOR ? null : vendorId;
  const selectedVendorCommission = vendors.find((v) => v.id === selectedVendorId)?.commissionPercent ?? null;
  const trimmedCommission = commissionInput.trim();
  const parsedCommission = trimmedCommission === "" ? null : Number(trimmedCommission);
  const commissionInvalid =
    trimmedCommission !== "" && (Number.isNaN(parsedCommission!) || parsedCommission! < 0 || parsedCommission! > 100);
  const selectedCommissionPercent: number | null | undefined = commissionInvalid
    ? undefined
    : trimmedCommission === ""
      ? null
      : parsedCommission;

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Atualizar cliente</DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="rounded-xl border border-border bg-secondary/20 p-8 text-center text-sm text-muted-foreground">
            <Loader2 className="mx-auto mb-3 h-5 w-5 animate-spin text-primary" />
            Carregando detalhes do cliente...
          </div>
        )}

        {error && !loading && <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">{error}</div>}

        {!loading && !error && client && (
          <>
            <div className="-mx-6 flex-1 space-y-5 overflow-y-auto px-6">
              <div className="rounded-xl border border-border bg-secondary/30 p-4">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-display text-lg font-semibold">
                      {client.profile?.tradeName || client.profile?.companyName || client.email}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">{client.email}</p>
                  </div>
                  <div className="ml-auto">
                    <StatusBadge status={client.status} incomplete={!client.profile} />
                  </div>
                </div>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                {[
                  { label: "Email", value: client.email, icon: Mail },
                  { label: "Perfil", value: client.role, icon: UserCheck },
                  { label: "Status comercial", value: client.status, icon: UserCheck },
                  { label: "Criado em", value: new Date(client.createdAt).toLocaleString("pt-BR"), icon: Calendar },
                  {
                    label: "Tabela de preço",
                    value: selectedPriceTableId
                      ? priceTables.find((table) => table.id === selectedPriceTableId)?.name ?? "Nao selecionada"
                      : "Sem tabela de preço",
                    icon: FileText,
                  },
                  {
                    label: "Vendedor",
                    value: selectedVendorId
                      ? vendors.find((vendor) => vendor.id === selectedVendorId)?.name ?? client.vendorName ?? "Nao selecionado"
                      : "Sem vendedor",
                    icon: User,
                  },
                  { label: legalNameLabel(clientType), value: client.profile?.companyName ?? "Nao informado", icon: Building2 },
                  // Nome fantasia só existe para pessoa jurídica.
                  ...(isPessoaFisica(clientType)
                    ? []
                    : [{ label: "Nome fantasia", value: client.profile?.tradeName ?? "Nao informado", icon: Building2 }]),
                  { label: documentLabel(clientType), value: client.profile?.cnpj ?? "Nao informado", icon: FileText },
                  { label: "Contato", value: client.profile?.contactName ?? "Nao informado", icon: User },
                  { label: "Telefone", value: client.profile?.phone ?? "Nao informado", icon: Phone },
                  { label: "Endereco", value: formatCompanyAddress(client.profile) ?? "Nao informado", icon: MapPin },
                ].map((row) => (
                  <DetailField key={row.label} label={row.label} value={row.value} icon={row.icon} />
                ))}
              </div>

              <div className="space-y-2">
                <Label htmlFor="price-table">Tabela de preços</Label>
                <Select
                  value={priceTableId}
                  onValueChange={setPriceTableId}
                  disabled={priceTablesStatus === "loading"}
                >
                  <SelectTrigger id="price-table" className="h-11">
                    <SelectValue placeholder={priceTablesStatus === "loading" ? "Carregando..." : "Sem tabela de preço"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_PRICE_TABLE}>Sem tabela de preço</SelectItem>
                    {priceTables.map((table) => (
                      <SelectItem key={table.id} value={table.id}>
                        {table.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="vendor">Vendedor responsável</Label>
                <Select
                  value={vendorId}
                  onValueChange={setVendorId}
                  disabled={vendorsStatus === "loading"}
                >
                  <SelectTrigger id="vendor" className="h-11">
                    <SelectValue placeholder={vendorsStatus === "loading" ? "Carregando..." : "Sem vendedor"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={NO_VENDOR}>Sem vendedor</SelectItem>
                    {vendors.map((vendor) => (
                      <SelectItem key={vendor.id} value={vendor.id}>
                        {vendor.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Atribuir o vendedor permite acompanhar pedidos e calcular comissões.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="commission-percent">% de comissão (override do cliente)</Label>
                <Input
                  id="commission-percent"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  className="h-11"
                  value={commissionInput}
                  onChange={(event) => setCommissionInput(event.target.value)}
                  placeholder={
                    selectedVendorCommission != null
                      ? `Padrão do vendedor: ${selectedVendorCommission}%`
                      : "Sem comissão configurada"
                  }
                />
                {commissionInvalid && (
                  <p className="text-xs text-destructive">Valor deve estar entre 0 e 100.</p>
                )}
                <p className="text-xs text-muted-foreground">
                  Deixe vazio para herdar o % padrão do vendedor. Apenas admins podem alterar.
                </p>
              </div>

              {hasPermission(useApp.getState().account, "CLIENTES.NEGOCIACAO.VISUALIZAR") && (
                <div className="space-y-2 rounded-xl border border-border bg-background/60 p-4">
                  <Label className="text-sm font-semibold">Condições da negociação</Label>
                  <p className="text-xs text-muted-foreground">
                    Comissões, impostos/despesas e margens por vigência. Referência para a rentabilidade (Etapa 5);
                    não altera preços nem condições financeiras.
                  </p>
                  <NegotiationConditionPanel
                    customerId={client.id}
                    canEdit={hasPermission(useApp.getState().account, "CLIENTES.NEGOCIACAO.EDITAR")}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="client-type">Tipo de cliente</Label>
                <Select value={clientType} onValueChange={(value) => setClientType(value as ClientType)}>
                  <SelectTrigger id="client-type" className="h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CLIENT_TYPE_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="rounded-lg border border-border bg-background/70 p-3">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="small-quantity-surcharge"
                    checked={surchargeEnabled}
                    onCheckedChange={(checked) => setSurchargeEnabled(checked === true)}
                    className="mt-0.5"
                  />
                  <div className="space-y-1">
                    <Label htmlFor="small-quantity-surcharge" className="cursor-pointer font-medium">
                      Esse cliente terá acréscimo de 10% para pedidos inferior a 1kg?
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Marcado por padrão (regra geral). Desmarque para isentar este cliente: nenhum pedido dele
                      receberá o acréscimo de venda fracionada.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-3 rounded-lg border border-border bg-background/70 p-3">
                <div>
                  <p className="font-medium">Condição financeira padrão</p>
                  <p className="text-xs text-muted-foreground">
                    Usada como padrão ao gerar títulos a receber. É <strong>congelada no título</strong> no
                    momento da criação — alterar aqui não muda títulos já existentes.
                  </p>
                </div>
                <div className="grid gap-3 md:grid-cols-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="fin-term">Prazo padrão (dias)</Label>
                    <Input
                      id="fin-term"
                      type="number"
                      min="0"
                      className="h-11"
                      placeholder="ex.: 28"
                      value={termDaysInput}
                      onChange={(event) => setTermDaysInput(event.target.value)}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="fin-method">Forma de pagamento</Label>
                    <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                      <SelectTrigger id="fin-method" className="h-11">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_METHOD}>Sem forma padrão</SelectItem>
                        <SelectItem value="BOLETO">Boleto</SelectItem>
                        <SelectItem value="PIX">PIX</SelectItem>
                        <SelectItem value="TRANSFERENCIA">Transferência/Depósito</SelectItem>
                        <SelectItem value="DINHEIRO">Dinheiro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="fin-installments">Parcelas padrão</Label>
                    <Input
                      id="fin-installments"
                      type="number"
                      min="1"
                      className="h-11"
                      value={installmentsInput}
                      onChange={(event) => setInstallmentsInput(event.target.value)}
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button variant="outline" onClick={() => void saveFinancialCondition()} disabled={savingCondition}>
                    {savingCondition && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Salvar condição financeira
                  </Button>
                </div>
              </div>
            </div>

            {!selectedPriceTableId && (
              <p className="text-xs text-amber-700 dark:text-amber-400">
                Selecione a tabela de preço para validar. O vendedor é opcional.
              </p>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose}>
                Cancelar
              </Button>
              <Button
                variant="hero"
                onClick={() => void onConfirm(client, "VALIDADO", clientType, selectedPriceTableId, selectedVendorId, selectedCommissionPercent, surchargeEnabled)}
                disabled={
                  commissionInvalid ||
                  priceTablesStatus === "loading" ||
                  !selectedPriceTableId
                }
              >
                <CheckCircle2 className="h-4 w-4" /> Validar
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function DetailField({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon: ComponentType<{ className?: string }>;
}) {
  return (
    <div className="rounded-lg border border-border bg-background/70 p-3">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="mt-1 break-words text-sm font-medium">{value}</div>
    </div>
  );
}

function getClientLabel(client: Pick<AdminClientSummary, "tradeName" | "companyName" | "email">) {
  return client.tradeName || client.companyName || client.email;
}

function EditClientDialog({
  open,
  client,
  loading,
  error,
  onClose,
  onSave,
}: {
  open: boolean;
  client: AdminClientDetail | null;
  loading: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (clientId: string, input: UpdateAdminCustomerProfileInput) => Promise<void>;
}) {
  const empty = {
    email: "",
    companyName: "",
    tradeName: "",
    cnpj: "",
    phone: "",
    contactName: "",
    addressCep: "",
    addressLogradouro: "",
    addressNumero: "",
    addressComplemento: "",
    addressBairro: "",
    addressCidade: "",
    addressUf: "",
    clientType: "RESTAURANTE" as ClientType,
  };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!client) {
      setForm(empty);
      return;
    }
    const profile = client.profile;
    setForm({
      email: client.email ?? "",
      companyName: profile?.companyName ?? "",
      tradeName: profile?.tradeName ?? "",
      cnpj: profile?.cnpj ?? "",
      phone: profile?.phone ?? "",
      contactName: profile?.contactName ?? "",
      addressCep: profile?.addressCep ?? "",
      addressLogradouro: profile?.addressLogradouro ?? "",
      addressNumero: profile?.addressNumero ?? "",
      addressComplemento: profile?.addressComplemento ?? "",
      addressBairro: profile?.addressBairro ?? "",
      addressCidade: profile?.addressCidade ?? "",
      addressUf: profile?.addressUf ?? "",
      clientType: profile?.clientType ?? client.clientType ?? "RESTAURANTE",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  const update = (field: keyof typeof empty) => (event: ChangeEvent<HTMLInputElement>) =>
    setForm((prev) => ({ ...prev, [field]: event.target.value }));

  const ufInvalid = form.addressUf.length > 0 && !/^[A-Za-z]{2}$/.test(form.addressUf);
  const emailInvalid = form.email.trim().length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim());
  const requiredFilled =
    form.email.trim() &&
    !emailInvalid &&
    form.companyName.trim() &&
    form.cnpj.trim() &&
    form.phone.trim() &&
    form.contactName.trim() &&
    form.addressCep.trim() &&
    form.addressLogradouro.trim() &&
    form.addressNumero.trim() &&
    form.addressBairro.trim() &&
    form.addressCidade.trim() &&
    /^[A-Za-z]{2}$/.test(form.addressUf);

  const handleSave = async () => {
    if (!client || !requiredFilled) return;
    setSaving(true);
    try {
      await onSave(client.id, {
        email: form.email.trim(),
        companyName: form.companyName.trim(),
        tradeName: form.tradeName.trim() || null,
        cnpj: form.cnpj.trim(),
        phone: form.phone.trim(),
        contactName: form.contactName.trim(),
        addressCep: form.addressCep.trim(),
        addressLogradouro: form.addressLogradouro.trim(),
        addressNumero: form.addressNumero.trim(),
        addressComplemento: form.addressComplemento.trim() || null,
        addressBairro: form.addressBairro.trim(),
        addressCidade: form.addressCidade.trim(),
        addressUf: form.addressUf.trim().toUpperCase(),
        clientType: form.clientType,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !saving && onClose()}>
      <DialogContent className="flex max-h-[90vh] max-w-3xl flex-col">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Editar cliente</DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="rounded-xl border border-border bg-secondary/20 p-8 text-center text-sm text-muted-foreground">
            <Loader2 className="mx-auto mb-3 h-5 w-5 animate-spin text-primary" />
            Carregando detalhes do cliente...
          </div>
        )}

        {error && !loading && (
          <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">{error}</div>
        )}

        {!loading && !error && client && (
          <>
            <div className="-mx-6 flex-1 space-y-4 overflow-y-auto px-6">
              <FormField label="E-mail" required>
                <Input type="email" value={form.email} onChange={update("email")} />
                {emailInvalid && <p className="mt-1 text-xs text-destructive">E-mail inválido.</p>}
              </FormField>

              <div className="grid gap-3 md:grid-cols-2">
                <FormField label={legalNameLabel(form.clientType)} required>
                  <Input value={form.companyName} onChange={update("companyName")} />
                </FormField>
                {!isPessoaFisica(form.clientType) && (
                  <FormField label="Nome fantasia">
                    <Input
                      value={form.tradeName}
                      onChange={update("tradeName")}
                      placeholder="Nome usado nas listas (opcional)"
                    />
                  </FormField>
                )}
                <FormField label={documentLabel(form.clientType)} required>
                  <Input value={form.cnpj} onChange={update("cnpj")} />
                </FormField>
                <FormField label="Contato" required>
                  <Input value={form.contactName} onChange={update("contactName")} />
                </FormField>
                <FormField label="Telefone" required>
                  <Input value={form.phone} onChange={update("phone")} />
                </FormField>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-client-type">Tipo de cliente</Label>
                <Select
                  value={form.clientType}
                  onValueChange={(value) => setForm((prev) => ({ ...prev, clientType: value as ClientType }))}
                >
                  <SelectTrigger id="edit-client-type" className="h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CLIENT_TYPE_LABELS).map(([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-3 md:grid-cols-[160px_1fr]">
                <FormField label="CEP" required>
                  <Input value={form.addressCep} onChange={update("addressCep")} />
                </FormField>
                <FormField label="Logradouro" required>
                  <Input value={form.addressLogradouro} onChange={update("addressLogradouro")} />
                </FormField>
              </div>

              <div className="grid gap-3 md:grid-cols-[140px_1fr]">
                <FormField label="Número" required>
                  <Input value={form.addressNumero} onChange={update("addressNumero")} />
                </FormField>
                <FormField label="Complemento">
                  <Input value={form.addressComplemento} onChange={update("addressComplemento")} />
                </FormField>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <FormField label="Bairro" required>
                  <Input value={form.addressBairro} onChange={update("addressBairro")} />
                </FormField>
                <FormField label="Cidade" required>
                  <Input value={form.addressCidade} onChange={update("addressCidade")} />
                </FormField>
                <FormField label="UF" required>
                  <Input
                    value={form.addressUf}
                    onChange={update("addressUf")}
                    maxLength={2}
                    className={ufInvalid ? "border-destructive" : undefined}
                  />
                  {ufInvalid && <p className="text-xs text-destructive">UF deve ter 2 letras.</p>}
                </FormField>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose} disabled={saving}>
                Cancelar
              </Button>
              <Button variant="hero" onClick={handleSave} disabled={!requiredFilled || saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pencil className="h-4 w-4" />} Salvar
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FormField({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">
        {label}
        {required && <span className="text-destructive"> *</span>}
      </Label>
      {children}
    </div>
  );
}
